// Admin users list — paginated table + search + create/edit/delete.
// Route is admin-only (guarded in App + sidebar hidden for managers); backend
// also enforces requireAdmin. The hidden root account never appears here.

import { useState } from 'react';
import {
  Button,
  Input,
  Popconfirm,
  Space,
  Table,
  Tag,
  Typography,
  message,
  type TablePaginationConfig,
} from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { AdminUser } from '../features/admin-users/admin-users.api';
import { useAdminUsers, useDeleteAdminUser } from '../features/admin-users/useAdminUsers';
import { UserFormModal } from '../features/admin-users/UserFormModal';
import { useAuth } from '../features/auth/useAuth';

const { Title } = Typography;
const { Search } = Input;

export function UsersListPage() {
  const { user: me } = useAuth();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<AdminUser | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  const { data, isLoading, isFetching } = useAdminUsers({
    page,
    limit,
    search: search || undefined,
    sort: '-createdAt',
  });
  const deleteMutation = useDeleteAdminUser();

  async function handleDelete(id: string) {
    try {
      await deleteMutation.mutateAsync(id);
      message.success('Đã xoá');
    } catch (err) {
      const apiMessage = (err as { response?: { data?: { message?: string } } }).response?.data?.message;
      message.error(apiMessage ?? 'Không xoá được');
    }
  }

  const columns: ColumnsType<AdminUser> = [
    { title: 'Tên đăng nhập', dataIndex: 'username', key: 'username' },
    {
      title: 'Vai trò',
      dataIndex: 'role',
      key: 'role',
      width: 120,
      render: (v: string) =>
        v === 'admin' ? <Tag color="purple">Admin</Tag> : <Tag color="blue">Manager</Tag>,
    },
    {
      title: 'Trạng thái',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 110,
      render: (v: boolean) => (v ? <Tag color="green">Active</Tag> : <Tag>Inactive</Tag>),
    },
    {
      title: 'Đăng nhập gần nhất',
      dataIndex: 'lastLoginAt',
      key: 'lastLoginAt',
      width: 170,
      render: (v: string | null) => (v ? new Date(v).toLocaleString('vi-VN') : '—'),
    },
    {
      title: 'Ngày tạo',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      render: (v: string) => new Date(v).toLocaleString('vi-VN'),
    },
    {
      title: '',
      key: 'actions',
      width: 100,
      render: (_, row) => {
        const isSelf = me?.id === row.id;
        return (
          <Space size={4}>
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => {
                setEditing(row);
                setModalOpen(true);
              }}
            />
            <Popconfirm
              title="Xoá tài khoản này?"
              okText="Xoá"
              cancelText="Huỷ"
              disabled={isSelf}
              onConfirm={() => void handleDelete(row.id)}
            >
              <Button type="text" danger icon={<DeleteOutlined />} disabled={isSelf} title={isSelf ? 'Không thể tự xoá' : 'Xoá'} />
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  const pagination: TablePaginationConfig = {
    current: page,
    pageSize: limit,
    total: data?.meta.total ?? 0,
    showSizeChanger: true,
    pageSizeOptions: [10, 20, 50, 100],
  };

  return (
    <>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>Quản lý người dùng</Title>
        <Space>
          <Search
            placeholder="Tìm theo tên đăng nhập"
            allowClear
            enterButton={<SearchOutlined />}
            onSearch={(v) => {
              setSearch(v);
              setPage(1);
            }}
            style={{ width: 260 }}
          />
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditing(null);
              setModalOpen(true);
            }}
          >
            Tạo tài khoản
          </Button>
        </Space>
      </Space>

      <Table<AdminUser>
        rowKey="id"
        columns={columns}
        dataSource={data?.data ?? []}
        loading={isLoading || isFetching}
        pagination={pagination}
        onChange={(pag) => {
          if (pag.current && pag.current !== page) setPage(pag.current);
          if (pag.pageSize && pag.pageSize !== limit) setLimit(pag.pageSize);
        }}
      />

      <UserFormModal
        open={modalOpen}
        editing={editing}
        onClose={() => {
          setModalOpen(false);
          setEditing(null);
        }}
      />
    </>
  );
}
